import { useState } from 'react';
import type { ReactNode, HTMLAttributes, Key } from 'react';
import {
  Box,
  Chip,
  Checkbox,
  Typography,
  Autocomplete,
  TextField,
  Button,
  ButtonGroup,
  IconButton,
  Tooltip,
  Popover,
  Badge,
  useMediaQuery,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import FilterListIcon from '@mui/icons-material/FilterList';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { format, isSameDay, differenceInDays } from 'date-fns';
import { useFilterStore } from '../../stores/filterStore';
import { useRequestStore } from '../../stores/requestStore';
import { REQUEST_TYPE_COLORS } from '../../theme';
import { formatOrgDisplayName } from '../../utils/display';
import { ALL_SURFACES, surfaceColor, surfaceLabel } from '../../utils/agentSurfaces';
import type { RequestType, FilterOption } from '../../types';

interface RequestTypeOption {
  value: RequestType;
  label: string;
}

const REQUEST_TYPES: RequestTypeOption[] = [
  { value: 'query', label: 'Query' },
  { value: 'workflow', label: 'Workflow' },
  { value: 'workflow_plan', label: 'Plan' },
  { value: 'agent', label: 'Agent' },
  { value: 'compare', label: 'Compare' },
];

const QUICK_RANGES = [
  { label: 'Today', days: 1 },
  { label: '3D', days: 3 },
  { label: '7D', days: 7 },
  { label: '14D', days: 14 },
  { label: '30D', days: 30 },
];

// In the "More filters" popover there's vertical room, so show all selected
// chips instead of collapsing to "+N" like the tight inline bar does.
const POPOVER_TAG_LIMIT = 20;

// Dark-theme styling for the Autocomplete options popup, shared by every multi-select.
const DROPDOWN_PAPER_SX = {
  bgcolor: '#1e293b',
  border: '1px solid rgba(148, 163, 184, 0.2)',
  borderRadius: 2,
  mt: 0.5,
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.45)',
  '& .MuiAutocomplete-listbox': { p: 0.5 },
  '& .MuiAutocomplete-option': { borderRadius: 1, minHeight: 32 },
  "& .MuiAutocomplete-option[aria-selected='true']": { bgcolor: 'rgba(139, 92, 246, 0.16)' },
  '& .MuiAutocomplete-option.Mui-focused': { bgcolor: 'rgba(139, 92, 246, 0.1)' },
};

interface MultiSelectFilterProps<T> {
  options: T[];
  value: T[];
  onChange: (value: T[]) => void;
  getOptionLabel: (option: T) => string;
  getOptionKey: (option: T) => string;
  placeholder?: string;
  minWidth?: number;
  maxWidth?: number;
  /** Number of chips shown before collapsing the rest into a "+N" chip. */
  limit?: number;
  optionIcon?: (option: T) => ReactNode;
}

/**
 * Compact multi-select used across the filter bar. Collapses selections to
 * `limit` chips + a "+N" chip (so the box never grows unbounded), keeps the
 * dropdown open while picking, and shows checkbox options in a styled popup.
 */
function MultiSelectFilter<T>({
  options,
  value,
  onChange,
  getOptionLabel,
  getOptionKey,
  placeholder = 'All',
  minWidth = 150,
  maxWidth = 260,
  limit = 1,
  optionIcon,
}: MultiSelectFilterProps<T>) {
  return (
    <Autocomplete
      multiple
      size="small"
      disableCloseOnSelect
      options={options}
      value={value}
      onChange={(_, next) => onChange(next)}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(a, b) => getOptionKey(a) === getOptionKey(b)}
      renderTags={(selected, getTagProps) => {
        const shown = selected.slice(0, limit);
        const extra = selected.length - shown.length;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap', minWidth: 0 }}>
            {shown.map((option, index) => {
              const { key: _key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  {...tagProps}
                  key={getOptionKey(option)}
                  label={getOptionLabel(option)}
                  size="small"
                  // Shrink/ellipsize the shown chip so the "+N" chip always fits.
                  sx={{ height: 20, fontSize: '0.7rem', flexShrink: 1, minWidth: 44, maxWidth: 120 }}
                />
              );
            })}
            {extra > 0 && (
              <Chip
                label={`+${extra}`}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  flexShrink: 0,
                  bgcolor: 'rgba(148, 163, 184, 0.2)',
                  color: 'text.secondary',
                }}
              />
            )}
          </Box>
        );
      }}
      renderOption={(props, option, { selected }) => {
        const { key: _key, ...liProps } = props as HTMLAttributes<HTMLLIElement> & {
          key?: Key;
        };
        return (
          <Box
            component="li"
            key={getOptionKey(option)}
            {...liProps}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: '0.8rem' }}
          >
            <Checkbox
              size="small"
              disableRipple
              checked={selected}
              icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
              checkedIcon={<CheckBoxIcon fontSize="small" />}
              sx={{ p: 0.25, color: 'rgba(148, 163, 184, 0.5)', '&.Mui-checked': { color: 'primary.main' } }}
            />
            {optionIcon?.(option)}
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {getOptionLabel(option)}
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField {...params} placeholder={value.length === 0 ? placeholder : ''} />
      )}
      slotProps={{ paper: { sx: DROPDOWN_PAPER_SX } }}
      sx={{
        minWidth,
        maxWidth,
        '& .MuiInputBase-root': { py: 0, fontSize: '0.75rem', flexWrap: 'nowrap', overflow: 'hidden' },
        // Keep the text input compact so the chip + "+N" always have room.
        '& .MuiAutocomplete-input': {
          py: '2px !important',
          minWidth: '24px !important',
          flex: '1 1 24px',
        },
      }}
    />
  );
}

export default function CompactFilters() {
  const isLargeScreen = useMediaQuery('(min-width: 1600px)'); // Custom breakpoint for wide monitors
  const [moreFiltersAnchor, setMoreFiltersAnchor] = useState<HTMLElement | null>(null);

  const { filters, availableOptions, availableAgentNames, setRequestTypes, setOrgIds, setUserIds, setTags, setFeedbackTypes, setChecklistOnly, setSurfaces, setAgentNames, setExcludeScheduled } = useFilterStore();
  const { dateRange, setDateRange, setQuickRange, goToPreviousDay, goToNextDay } = useRequestStore();

  const handleSurfaceToggle = (surface: string) => {
    const current = filters.surfaces;
    setSurfaces(current.includes(surface) ? current.filter((s) => s !== surface) : [...current, surface]);
  };
  const isSurfaceSelected = (surface: string) => filters.surfaces.includes(surface);
  // Show the surface sub-filter when Agent is in scope (selected, or an agent-only filter active).
  const showSurfaceFilter =
    filters.requestTypes.includes('agent') ||
    filters.surfaces.length > 0 ||
    (filters.agentNames?.length ?? 0) > 0 ||
    Boolean(filters.excludeScheduled);

  const handleTypeToggle = (type: RequestType) => {
    const current = filters.requestTypes;
    if (current.includes(type)) {
      setRequestTypes(current.filter((t) => t !== type));
    } else {
      setRequestTypes([...current, type]);
    }
  };

  const handleFeedbackToggle = (feedbackType: string) => {
    const current = filters.feedbackTypes;
    if (current.includes(feedbackType)) {
      setFeedbackTypes(current.filter((t) => t !== feedbackType));
    } else {
      setFeedbackTypes([...current, feedbackType]);
    }
  };

  const isTypeSelected = (type: RequestType) => filters.requestTypes.includes(type);
  const isFeedbackSelected = (feedbackType: string) => filters.feedbackTypes.includes(feedbackType);

  const selectedOrgs = availableOptions.orgIds.filter(o => filters.orgIds.includes(o.id));
  const selectedUsers = availableOptions.userIds.filter(u => filters.userIds.includes(u.id));

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value) {
      const newStart = new Date(e.target.value);
      setDateRange(newStart, dateRange.end, 'Custom');
    }
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value) {
      const newEnd = new Date(e.target.value);
      newEnd.setHours(23, 59, 59, 999);
      setDateRange(dateRange.start, newEnd, 'Custom');
    }
  };

  const isQuickRangeActive = (range: { days: number }): boolean => {
    const now = new Date();
    const daysDiff = differenceInDays(dateRange.end, dateRange.start) + 1;
    return daysDiff === range.days && isSameDay(dateRange.end, now);
  };

  const handleQuickRangeClick = (range: { days: number }) => {
    setQuickRange(range.days);
  };

  const isAtToday = isSameDay(dateRange.end, new Date());

  // Count active "more" filters for badge
  const moreFiltersCount = filters.orgIds.length + filters.userIds.length + filters.tags.length + filters.feedbackTypes.length + (filters.checklistOnly ? 1 : 0);

  // Shared filter components
  const OrgFilter = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography variant="caption" color="text.secondary">
        Org:
      </Typography>
      <MultiSelectFilter
        options={availableOptions.orgIds}
        value={selectedOrgs}
        onChange={(v) => setOrgIds(v.map((o) => o.id))}
        getOptionLabel={(o: FilterOption) => formatOrgDisplayName(o.name)}
        getOptionKey={(o: FilterOption) => o.id}
      />
    </Box>
  );

  const UserFilter = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography variant="caption" color="text.secondary">
        User:
      </Typography>
      <MultiSelectFilter
        options={availableOptions.userIds}
        value={selectedUsers}
        onChange={(v) => setUserIds(v.map((u) => u.id))}
        getOptionLabel={(u: FilterOption) => u.name}
        getOptionKey={(u: FilterOption) => u.id}
      />
    </Box>
  );

  const TagsFilter = availableOptions.tags.length > 0 && (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Tooltip title="Filter by user tags">
        <LocalOfferIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
      </Tooltip>
      <MultiSelectFilter
        options={availableOptions.tags}
        value={filters.tags}
        onChange={setTags}
        getOptionLabel={(t: string) => t}
        getOptionKey={(t: string) => t}
        placeholder="Tags"
        minWidth={120}
        optionIcon={() => <LocalOfferIcon sx={{ fontSize: 14, color: '#a855f7' }} />}
      />
    </Box>
  );

  const FeedbackFilter = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography variant="caption" color="text.secondary">
        Feedback:
      </Typography>
      <Tooltip title="Show positive feedback">
        <Chip
          icon={<ThumbUpIcon sx={{ fontSize: '14px !important' }} />}
          size="small"
          onClick={() => handleFeedbackToggle('positive')}
          sx={{
            height: 22,
            minWidth: 32,
            bgcolor: isFeedbackSelected('positive') ? 'success.main' : 'transparent',
            border: '1px solid',
            borderColor: 'success.main',
            '& .MuiChip-icon': {
              color: isFeedbackSelected('positive') ? 'white' : 'success.main',
              m: 0,
            },
            '& .MuiChip-label': {
              display: 'none',
            },
            '&:hover': {
              bgcolor: isFeedbackSelected('positive') ? 'success.main' : 'rgba(76, 175, 80, 0.1)',
            },
          }}
        />
      </Tooltip>
      <Tooltip title="Show negative feedback">
        <Chip
          icon={<ThumbDownIcon sx={{ fontSize: '14px !important' }} />}
          size="small"
          onClick={() => handleFeedbackToggle('negative')}
          sx={{
            height: 22,
            minWidth: 32,
            bgcolor: isFeedbackSelected('negative') ? 'error.main' : 'transparent',
            border: '1px solid',
            borderColor: 'error.main',
            '& .MuiChip-icon': {
              color: isFeedbackSelected('negative') ? 'white' : 'error.main',
              m: 0,
            },
            '& .MuiChip-label': {
              display: 'none',
            },
            '&:hover': {
              bgcolor: isFeedbackSelected('negative') ? 'error.main' : 'rgba(244, 67, 54, 0.1)',
            },
          }}
        />
      </Tooltip>
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.5,
        rowGap: 1,
        flexWrap: 'wrap',
        py: 1,
        px: 2,
        bgcolor: 'rgba(30, 41, 59, 0.5)',
        borderRadius: 1,
        mb: 2,
      }}
    >
      {/* Time Range */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          Time:
        </Typography>
        <ButtonGroup size="small" variant="outlined">
          {QUICK_RANGES.map((range) => (
            <Button
              key={range.label}
              onClick={() => handleQuickRangeClick(range)}
              variant={isQuickRangeActive(range) ? 'contained' : 'outlined'}
              sx={{ minWidth: 36, px: 0.75, fontSize: '0.7rem' }}
            >
              {range.label}
            </Button>
          ))}
        </ButtonGroup>
        <IconButton size="small" onClick={goToPreviousDay} sx={{ p: 0.25 }}>
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <TextField
          type="date"
          size="small"
          value={format(dateRange.start, 'yyyy-MM-dd')}
          onChange={handleStartDateChange}
          sx={{ width: 120, '& .MuiInputBase-input': { py: 0.5, fontSize: '0.75rem' } }}
        />
        <Typography variant="caption" color="text.secondary">–</Typography>
        <TextField
          type="date"
          size="small"
          value={format(dateRange.end, 'yyyy-MM-dd')}
          onChange={handleEndDateChange}
          sx={{ width: 120, '& .MuiInputBase-input': { py: 0.5, fontSize: '0.75rem' } }}
        />
        <IconButton size="small" onClick={goToNextDay} disabled={isAtToday} sx={{ p: 0.25 }}>
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Divider */}
      <Typography sx={{ color: 'rgba(148, 163, 184, 0.4)' }}>|</Typography>

      {/* Request Types */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          Type:
        </Typography>
        {REQUEST_TYPES.map((type) => (
          <Chip
            key={type.value}
            label={type.label}
            size="small"
            onClick={() => handleTypeToggle(type.value)}
            sx={{
              height: 22,
              fontSize: '0.7rem',
              bgcolor: isTypeSelected(type.value) ? REQUEST_TYPE_COLORS[type.value] : 'transparent',
              color: isTypeSelected(type.value) ? 'white' : REQUEST_TYPE_COLORS[type.value],
              border: `1px solid ${REQUEST_TYPE_COLORS[type.value]}`,
              '&:hover': {
                bgcolor: isTypeSelected(type.value)
                  ? REQUEST_TYPE_COLORS[type.value]
                  : `${REQUEST_TYPE_COLORS[type.value]}20`,
              },
            }}
          />
        ))}
      </Box>

      {/* Surface sub-filter (agent rows only) */}
      {showSurfaceFilter && (
        <>
          <Typography sx={{ color: 'rgba(148, 163, 184, 0.4)' }}>|</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Surface:
            </Typography>
            {ALL_SURFACES.map((surface) => (
              <Chip
                key={surface}
                label={surfaceLabel(surface)}
                size="small"
                onClick={() => handleSurfaceToggle(surface)}
                sx={{
                  height: 22,
                  fontSize: '0.7rem',
                  bgcolor: isSurfaceSelected(surface) ? surfaceColor(surface) : 'transparent',
                  color: isSurfaceSelected(surface) ? 'white' : surfaceColor(surface),
                  border: `1px solid ${surfaceColor(surface)}`,
                  '&:hover': {
                    bgcolor: isSurfaceSelected(surface) ? surfaceColor(surface) : `${surfaceColor(surface)}20`,
                  },
                }}
              />
            ))}
            {/* Negative filter — dashed border + calendar-off icon set it apart
                from the solid "Scheduled" surface chip, which does the opposite. */}
            <Tooltip title="Hide scheduled agent runs (they can be noisy)">
              <Chip
                icon={<EventBusyIcon sx={{ fontSize: '14px !important' }} />}
                label="Exclude scheduled"
                size="small"
                onClick={() => setExcludeScheduled(!filters.excludeScheduled)}
                sx={{
                  height: 22,
                  fontSize: '0.7rem',
                  ml: 0.5,
                  bgcolor: filters.excludeScheduled ? '#64748b' : 'transparent',
                  color: filters.excludeScheduled ? 'white' : 'text.secondary',
                  border: '1px dashed rgba(148, 163, 184, 0.6)',
                  '& .MuiChip-icon': { color: filters.excludeScheduled ? 'white' : 'text.secondary' },
                  '&:hover': {
                    bgcolor: filters.excludeScheduled ? '#64748b' : 'rgba(148, 163, 184, 0.12)',
                  },
                }}
              />
            </Tooltip>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Agent:
              </Typography>
              <MultiSelectFilter
                options={availableAgentNames}
                value={filters.agentNames ?? []}
                onChange={setAgentNames}
                getOptionLabel={(a: string) => a}
                getOptionKey={(a: string) => a}
                minWidth={160}
              />
            </Box>
          </Box>
        </>
      )}

      {/* Large screen: show all filters inline */}
      {isLargeScreen ? (
        <>
          {/* Divider */}
          <Typography sx={{ color: 'rgba(148, 163, 184, 0.4)' }}>|</Typography>

          {OrgFilter}
          {UserFilter}
          {TagsFilter}

          {/* Divider */}
          <Typography sx={{ color: 'rgba(148, 163, 184, 0.4)' }}>|</Typography>

          {FeedbackFilter}

          <Tooltip title="Show only requests from checklists">
            <Chip
              icon={<PlaylistAddCheckIcon sx={{ fontSize: '14px !important' }} />}
              label="Checklist"
              size="small"
              onClick={() => setChecklistOnly(!filters.checklistOnly)}
              sx={{
                height: 22,
                fontSize: '0.7rem',
                bgcolor: filters.checklistOnly ? 'rgba(20, 184, 166, 0.25)' : 'transparent',
                border: '1px solid',
                borderColor: '#14b8a6',
                color: '#14b8a6',
                '& .MuiChip-icon': {
                  color: '#14b8a6',
                },
                '&:hover': {
                  bgcolor: filters.checklistOnly ? 'rgba(20, 184, 166, 0.35)' : 'rgba(20, 184, 166, 0.1)',
                },
              }}
            />
          </Tooltip>
        </>
      ) : (
        <>
          {/* Small screen: "More Filters" button */}
          <Typography sx={{ color: 'rgba(148, 163, 184, 0.4)' }}>|</Typography>

          <Tooltip title="More filters">
            <Badge badgeContent={moreFiltersCount} color="primary" max={9}>
              <Chip
                icon={<FilterListIcon sx={{ fontSize: '16px !important' }} />}
                label="More"
                size="small"
                onClick={(e) => setMoreFiltersAnchor(e.currentTarget)}
                sx={{
                  height: 24,
                  fontSize: '0.7rem',
                  bgcolor: moreFiltersCount > 0 ? 'rgba(99, 102, 241, 0.15)' : 'transparent',
                  border: '1px solid',
                  borderColor: moreFiltersCount > 0 ? 'primary.main' : 'rgba(148, 163, 184, 0.4)',
                  color: moreFiltersCount > 0 ? 'primary.main' : 'text.secondary',
                  '&:hover': {
                    bgcolor: 'rgba(99, 102, 241, 0.1)',
                  },
                }}
              />
            </Badge>
          </Tooltip>

          <Popover
            open={Boolean(moreFiltersAnchor)}
            anchorEl={moreFiltersAnchor}
            onClose={() => setMoreFiltersAnchor(null)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            <Box
              sx={{
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                minWidth: 320,
                bgcolor: 'background.paper',
              }}
            >
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                Additional Filters
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '70px 1fr', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">Org:</Typography>
                <MultiSelectFilter
                  options={availableOptions.orgIds}
                  value={selectedOrgs}
                  onChange={(v) => setOrgIds(v.map((o) => o.id))}
                  getOptionLabel={(o: FilterOption) => formatOrgDisplayName(o.name)}
                  getOptionKey={(o: FilterOption) => o.id}
                  minWidth={0}
                  maxWidth={9999}
                  limit={POPOVER_TAG_LIMIT}
                />

                <Typography variant="caption" color="text.secondary">User:</Typography>
                <MultiSelectFilter
                  options={availableOptions.userIds}
                  value={selectedUsers}
                  onChange={(v) => setUserIds(v.map((u) => u.id))}
                  getOptionLabel={(u: FilterOption) => u.name}
                  getOptionKey={(u: FilterOption) => u.id}
                  minWidth={0}
                  maxWidth={9999}
                  limit={POPOVER_TAG_LIMIT}
                />

                {availableOptions.tags.length > 0 && (
                  <>
                    <Typography variant="caption" color="text.secondary">Tags:</Typography>
                    <MultiSelectFilter
                      options={availableOptions.tags}
                      value={filters.tags}
                      onChange={setTags}
                      getOptionLabel={(t: string) => t}
                      getOptionKey={(t: string) => t}
                      minWidth={0}
                      maxWidth={9999}
                      limit={POPOVER_TAG_LIMIT}
                      optionIcon={() => <LocalOfferIcon sx={{ fontSize: 14, color: '#a855f7' }} />}
                    />
                  </>
                )}

                <Typography variant="caption" color="text.secondary">Feedback:</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Tooltip title="Show positive feedback">
                    <Chip
                      icon={<ThumbUpIcon sx={{ fontSize: '14px !important' }} />}
                      size="small"
                      onClick={() => handleFeedbackToggle('positive')}
                      sx={{
                        height: 22,
                        minWidth: 32,
                        bgcolor: isFeedbackSelected('positive') ? 'success.main' : 'transparent',
                        border: '1px solid',
                        borderColor: 'success.main',
                        '& .MuiChip-icon': {
                          color: isFeedbackSelected('positive') ? 'white' : 'success.main',
                          m: 0,
                        },
                        '& .MuiChip-label': {
                          display: 'none',
                        },
                        '&:hover': {
                          bgcolor: isFeedbackSelected('positive') ? 'success.main' : 'rgba(76, 175, 80, 0.1)',
                        },
                      }}
                    />
                  </Tooltip>
                  <Tooltip title="Show negative feedback">
                    <Chip
                      icon={<ThumbDownIcon sx={{ fontSize: '14px !important' }} />}
                      size="small"
                      onClick={() => handleFeedbackToggle('negative')}
                      sx={{
                        height: 22,
                        minWidth: 32,
                        bgcolor: isFeedbackSelected('negative') ? 'error.main' : 'transparent',
                        border: '1px solid',
                        borderColor: 'error.main',
                        '& .MuiChip-icon': {
                          color: isFeedbackSelected('negative') ? 'white' : 'error.main',
                          m: 0,
                        },
                        '& .MuiChip-label': {
                          display: 'none',
                        },
                        '&:hover': {
                          bgcolor: isFeedbackSelected('negative') ? 'error.main' : 'rgba(244, 67, 54, 0.1)',
                        },
                      }}
                    />
                  </Tooltip>
                </Box>

                <Typography variant="caption" color="text.secondary">Checklist:</Typography>
                <Tooltip title="Show only requests from checklists">
                  <Chip
                    icon={<PlaylistAddCheckIcon sx={{ fontSize: '14px !important' }} />}
                    label="Checklist Only"
                    size="small"
                    onClick={() => setChecklistOnly(!filters.checklistOnly)}
                    sx={{
                      height: 22,
                      fontSize: '0.7rem',
                      bgcolor: filters.checklistOnly ? 'rgba(20, 184, 166, 0.25)' : 'transparent',
                      border: '1px solid',
                      borderColor: '#14b8a6',
                      color: '#14b8a6',
                      '& .MuiChip-icon': {
                        color: '#14b8a6',
                      },
                      '&:hover': {
                        bgcolor: filters.checklistOnly ? 'rgba(20, 184, 166, 0.35)' : 'rgba(20, 184, 166, 0.1)',
                      },
                    }}
                  />
                </Tooltip>
              </Box>
            </Box>
          </Popover>
        </>
      )}

      {/* Clear All */}
      {(filters.requestTypes.length > 0 || filters.orgIds.length > 0 || filters.userIds.length > 0 || filters.tags.length > 0 || filters.feedbackTypes.length > 0 || filters.checklistOnly || filters.surfaces.length > 0 || (filters.agentNames?.length ?? 0) > 0 || filters.excludeScheduled) && (
        <Chip
          label="Clear"
          size="small"
          variant="outlined"
          onClick={() => {
            setRequestTypes([]);
            setOrgIds([]);
            setUserIds([]);
            setTags([]);
            setFeedbackTypes([]);
            setChecklistOnly(false);
            setSurfaces([]);
            setAgentNames([]);
            setExcludeScheduled(false);
          }}
          sx={{ height: 22, fontSize: '0.7rem' }}
        />
      )}
    </Box>
  );
}
